import { Head, Link, router, useForm, usePage } from "@inertiajs/react";
import {
  ArrowLeft,
  CalendarClock,
  Copy,
  KeyRound,
  Mail,
  Trash2,
  User as UserIcon,
  Users2,
} from "lucide-react";
import { type ComponentType, useEffect, useMemo, useState } from "react";

import InlineEditableField from "@/components/inline-editable-field";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import AppLayout from "@/layouts/app-layout";
import { resetPasswordUserPath, userPath, usersPath } from "@/routes";
import type { BreadcrumbItem, SharedData, User } from "@/types";

import type { UserGroupOption } from "./types";

interface UserShowProps {
  user: User & {
    last_sign_in_at?: string | null;
    deactivated_at?: string | null;
  };
  groups: UserGroupOption[];
}

function InfoRow({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string | null | undefined;
  icon: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/15 p-3">
      <Icon className="mt-1 h-4 w-4 text-muted-foreground" />
      <div>
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <p className="text-sm font-medium">
          {value && value.length > 0 ? value : "—"}
        </p>
      </div>
    </div>
  );
}

export default function UserShow({ user, groups }: UserShowProps) {
  const { props: pageProps } = usePage<SharedData>();
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const selfId = pageProps?.auth?.user?.id ?? null;
  const isSelf = selfId === user.id;

  const temporaryPassword = pageProps.flash?.temporary_password ?? null;
  const [temporaryPasswordCopied, setTemporaryPasswordCopied] = useState(false);

  // Reset the "copied" indicator when a new temporary password arrives
  useEffect(() => {
    if (!temporaryPassword) return;
    setTemporaryPasswordCopied(false);
  }, [temporaryPassword]);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Usuarios",
      href: usersPath(),
    },
    {
      title: user.full_name ?? user.name ?? user.email,
      href: "#",
    },
  ];

  const displayName = user.full_name ?? user.name ?? "Usuario sin nombre";

  const lastSignIn = user.last_sign_in_at
    ? new Date(user.last_sign_in_at).toLocaleString()
    : null;
  const createdAt = user.created_at
    ? new Date(user.created_at).toLocaleString()
    : null;
  const updatedAt = user.updated_at
    ? new Date(user.updated_at).toLocaleString()
    : null;

  const initialGroupIds = useMemo(
    () =>
      groups
        .filter((group) => group.assigned)
        .map((group) => group.id)
        .sort((a, b) => a - b),
    [groups],
  );

  const {
    data,
    setData,
    processing: groupsProcessing,
    recentlySuccessful,
    isDirty: groupsDirty,
  } = useForm<{ group_ids: number[] }>({
    group_ids: initialGroupIds,
  });

  useEffect(() => {
    setData("group_ids", initialGroupIds);
  }, [initialGroupIds, setData]);

  const toggleGroup = (groupId: number) => {
    const currentIds = Array.isArray(data.group_ids) ? data.group_ids : [];
    if (currentIds.includes(groupId)) {
      setData(
        "group_ids",
        currentIds.filter((id) => id !== groupId),
      );
    } else {
      setData("group_ids", [...currentIds, groupId]);
    }
  };

  const handleGroupSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!groupsDirty) return;
    router.put(
      userPath(user),
      {
        user: {
          group_ids: data.group_ids,
        },
      },
      {
        preserveScroll: true,
      },
    );
  };

  const handleDelete = () => {
    if (
      window.confirm(
        "¿Estás seguro de que querés desactivar este usuario? Podrás reactivarlo más adelante.",
      )
    ) {
      router.delete(userPath(user));
    }
  };

  const handleResetPassword = () => {
    router.post(
      resetPasswordUserPath(user),
      {},
      {
        onSuccess: () => setResetPasswordOpen(false),
      },
    );
  };

  const handleCopyTemporaryPassword = async () => {
    if (!temporaryPassword) return;

    try {
      await navigator.clipboard.writeText(temporaryPassword);
      setTemporaryPasswordCopied(true);
    } catch {
      setTemporaryPasswordCopied(false);
    }
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Usuario: ${displayName}`} />

      <AlertDialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Restablecer contraseña?</AlertDialogTitle>
            <AlertDialogDescription>
              Se va a generar una contraseña temporal.
              Vas a poder verla una sola vez cuando vuelvas a esta pantalla.
              Copiala y compartila con el usuario, porque si recargás la página no
              la vas a poder ver de nuevo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetPassword}>
              Restablecer contraseña
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-2">
              <Link href={usersPath()}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver a Usuarios
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">{displayName}</h1>
            <p className="text-muted-foreground">
              Gestiona la información y permisos del usuario seleccionado.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserIcon className="h-5 w-5" />
              Información general
            </CardTitle>
            <CardDescription>
              Usa los campos editables para actualizar nombre, email y estado de
              verificación.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InlineEditableField
                label="Nombre completo"
                value={user.full_name}
                field="full_name"
                updateUrl={userPath(user)}
                icon={UserIcon}
                placeholder="Nombre y apellido"
              />

              <InlineEditableField
                label="Email"
                value={user.email}
                field="email"
                updateUrl={userPath(user)}
                icon={Mail}
                placeholder="usuario@empresa.com"
                disabled={true}
              />
            </div>

            <Separator />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <InfoRow
                label="Último acceso"
                value={lastSignIn}
                icon={CalendarClock}
              />
              <InfoRow label="Creado" value={createdAt} icon={CalendarClock} />
              <InfoRow
                label="Actualizado"
                value={updatedAt}
                icon={CalendarClock}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users2 className="h-5 w-5" />
              Grupos y permisos
            </CardTitle>
            <CardDescription>
              Selecciona los grupos que definen el nivel de acceso del usuario.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {groups.length > 0 ? (
              <form onSubmit={handleGroupSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {groups.map((group) => {
                    const groupIds = Array.isArray(data.group_ids)
                      ? data.group_ids
                      : [];
                    const checked = groupIds.includes(group.id);
                    return (
                      <label
                        key={group.id}
                        className="flex items-center gap-3 rounded-lg border border-border/70 bg-muted/10 p-3"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleGroup(group.id)}
                          disabled={groupsProcessing}
                        />
                        <div>
                          <p className="text-sm font-medium leading-none">
                            {group.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {group.key}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    type="submit"
                    disabled={!groupsDirty || groupsProcessing}
                  >
                    Guardar permisos
                  </Button>
                  {recentlySuccessful && (
                    <span className="text-sm text-muted-foreground">
                      Permisos actualizados.
                    </span>
                  )}
                </div>
              </form>
            ) : (
              <p className="text-sm text-muted-foreground">
                No hay grupos configurados todavía.
              </p>
            )}
          </CardContent>
        </Card>

        {temporaryPassword && (
          <Alert className="border-yellow-300 bg-yellow-50 text-yellow-950">
            <AlertTitle>Contraseña temporal generada</AlertTitle>
            <AlertDescription className="space-y-3 text-yellow-950">
              <p>
                Copiá y compartí esta contraseña con el usuario ahora.
                Si recargás la página, no vas a poder verla de nuevo.
              </p>
              <div className="flex flex-col gap-3 rounded-md border border-yellow-200 bg-white/60 p-3 md:flex-row md:items-center md:justify-between">
                <code className="text-sm font-mono">{temporaryPassword}</code>
                <Button
                  type="button"
                  variant="outline"
                  className="border-yellow-300 bg-white hover:bg-yellow-100"
                  onClick={handleCopyTemporaryPassword}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {temporaryPasswordCopied ? "Copiada" : "Copiar"}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Seguridad de la cuenta
            </CardTitle>
            <CardDescription>
              Acciones de seguridad para la cuenta del usuario.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={() => setResetPasswordOpen(true)}
              disabled={isSelf}
            >
              <KeyRound className="mr-2 h-4 w-4" />
              Restablecer contraseña
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Zona peligrosa</CardTitle>
            <CardDescription>
              Desactiva al usuario para revocar su acceso. Podrás reactivarlo
              manualmente.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isSelf}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {isSelf ? "No puedes desactivarte" : "Desactivar usuario"}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </AppLayout>
  );
}
